import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GlobeIcon, UsersIcon, PackageIcon, TrendingUpIcon } from 'lucide-react';
export function RetailNetwork() {
  const [counters, setCounters] = useState({
    suppliers: 0,
    countries: 0,
    continents: 0,
    years: 0
  });
  const stats = [{
    key: 'suppliers',
    target: 500,
    label: 'Verified Suppliers',
    icon: UsersIcon,
    suffix: '+'
  }, {
    key: 'countries',
    target: 30,
    label: 'Countries Served',
    icon: GlobeIcon,
    suffix: '+'
  }, {
    key: 'continents',
    target: 5,
    label: 'Continents',
    icon: PackageIcon,
    suffix: ''
  }, {
    key: 'years',
    target: 10,
    label: 'Years Experience',
    icon: TrendingUpIcon,
    suffix: '+'
  }];
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    const timers = stats.map(stat => {
      const increment = stat.target / steps;
      let current = 0;
      return setInterval(() => {
        current += increment;
        if (current >= stat.target) {
          setCounters(prev => ({
            ...prev,
            [stat.key]: stat.target
          }));
          clearInterval(timers[stats.indexOf(stat)]);
        } else {
          setCounters(prev => ({
            ...prev,
            [stat.key]: Math.floor(current)
          }));
        }
      }, interval);
    });
    return () => timers.forEach(clearInterval);
  }, []);
  const regions = [{
    region: 'Africa',
    flagship: true,
    description: 'Flagship focus across ECOWAS, EAC, and SADC blocs. Active in infrastructure, healthcare, agricultural, and ICT procurement programmes, including African Union (AU)-funded and multilateral initiatives.',
    countries: ['Nigeria', 'Ghana', 'Kenya', 'South Africa', "Côte d'Ivoire", 'Ethiopia']
  }, {
    region: 'Europe',
    description: 'UK headquarters with established networks across the EU. Specialists in regulatory compliance, public sector tenders, and European supplier coordination.',
    countries: ['United Kingdom', 'Germany', 'France', 'Netherlands', 'Spain', 'Italy']
  }, {
    region: 'Asia-Pacific',
    description: 'Deep manufacturer relationships across major production hubs. Strong sourcing capability in industrial equipment, electronics, textiles, and consumer goods.',
    countries: ['China', 'India', 'Vietnam', 'Thailand', 'Malaysia', 'Indonesia']
  }, {
    region: 'Middle East',
    description: 'GCC market expertise supporting large-scale infrastructure, energy, and government procurement programmes across the Gulf region.',
    countries: ['UAE', 'Saudi Arabia', 'Qatar', 'Oman', 'Kuwait', 'Bahrain']
  }, {
    region: 'Americas',
    description: 'Cross-continental procurement support across North and Latin America, with focus on industrial equipment, energy, and commercial goods.',
    countries: ['United States', 'Canada', 'Mexico', 'Brazil', 'Colombia', 'Chile']
  }];
  return <section id="global-presence" className="py-24 px-6 bg-navy-dark relative overflow-hidden" data-id="element-234">
      {/* Background Map Dots — spread globally */}
      <div className="absolute inset-0 opacity-10" data-id="element-235">
        <svg className="w-full h-full" viewBox="0 0 1000 600" data-id="element-236">
          {/* Europe / UK */}
          <circle cx="490" cy="200" r="6" fill="#c8924a" data-id="element-237">
            <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" data-id="element-238" />
          </circle>
          {/* Africa */}
          <circle cx="510" cy="370" r="8" fill="#c8924a" data-id="element-239">
            <animate attributeName="r" values="8;12;8" dur="2s" repeatCount="indefinite" begin="0.3s" data-id="element-240" />
          </circle>
          {/* Middle East */}
          <circle cx="600" cy="290" r="6" fill="#c8924a" data-id="element-241">
            <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" begin="0.6s" data-id="element-242" />
          </circle>
          {/* Asia */}
          <circle cx="750" cy="280" r="6" fill="#c8924a" data-id="element-243">
            <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" begin="0.9s" data-id="element-244" />
          </circle>
          {/* Americas */}
          <circle cx="220" cy="280" r="6" fill="#c8924a" data-id="element-245">
            <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" begin="1.2s" data-id="element-246" />
          </circle>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-247">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-248">
          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-249">
            <div className="w-12 h-px bg-gold" data-id="element-250"></div>
            <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-251">
              Global Presence
            </span>
            <div className="w-12 h-px bg-gold" data-id="element-252"></div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-semibold text-white mb-4" data-id="element-253">
            Worldwide Reach, Regional Expertise
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto" data-id="element-254">
            Operating across five continents with established procurement
            networks and a flagship focus on Africa and high-growth emerging
            markets
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16" data-id="element-255">
          {stats.map((stat, i) => <motion.div key={stat.key} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="text-center" data-id="element-256">
              <div className="w-16 h-16 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-257">
                <stat.icon size={28} className="text-gold" data-id="element-258" />
              </div>
              <div className="text-5xl font-bold text-white mb-2" data-id="element-259">
                {counters[stat.key]}
                {stat.suffix}
              </div>
              <div className="text-slate-400 font-medium" data-id="element-260">{stat.label}</div>
            </motion.div>)}
        </div>

        {/* Regions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-261">
          {regions.map((region, i) => <motion.div key={region.region} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className={`bg-navy-light p-6 rounded-lg ${region.flagship ? 'border-2 border-gold/40 lg:col-span-1' : 'border border-slate-800'}`} data-id="element-262">
              <div className="flex items-center gap-2 mb-3" data-id="element-263">
                <h3 className="text-xl font-semibold text-white" data-id="element-264">
                  {region.region}
                </h3>
                {region.flagship && <span className="text-xs bg-gold/20 text-gold px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider" data-id="element-265">
                    Flagship
                  </span>}
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mb-4" data-id="element-266">
                {region.description}
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-267">
                {region.countries.map(country => <span key={country} className="text-xs bg-navy-dark border border-slate-800 text-slate-300 px-3 py-1 rounded-full" data-id="element-268">
                    {country}
                  </span>)}
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}